/**
 * Fires when the user returns to this tab after it was hidden, or when the page is restored
 * from the back/forward cache. Subscribe once per feature that must resync server state.
 */
export type PortalResumeReason = 'visibility' | 'pageshow';

export type PortalResumeDetail = {
  reason: PortalResumeReason;
  /**
   * Time the document was hidden (ms). `null` for bfcache `pageshow` where duration is unknown;
   * treat as “long enough” for min-hidden gates.
   */
  hiddenDurationMs: number | null;
};

const listeners = new Set<(detail: PortalResumeDetail) => void>();

let attached = false;
/** True after we've seen `visibilityState === 'hidden'` at least once (or started hidden). */
let wasHiddenOrBackground = typeof document !== 'undefined' && document.visibilityState === 'hidden';
let hiddenAt: number | null =
  typeof document !== 'undefined' && document.visibilityState === 'hidden' ? Date.now() : null;

function notify(detail: PortalResumeDetail) {
  listeners.forEach((fn) => {
    try {
      fn(detail);
    } catch {
      // isolate subscriber errors
    }
  });
}

function ensureListeners() {
  if (attached || typeof document === 'undefined') return;
  attached = true;

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      wasHiddenOrBackground = true;
      hiddenAt = Date.now();
      return;
    }
    if (document.visibilityState === 'visible' && wasHiddenOrBackground) {
      wasHiddenOrBackground = false;
      const duration =
        hiddenAt != null ? Math.max(0, Date.now() - hiddenAt) : 0;
      hiddenAt = null;
      notify({ reason: 'visibility', hiddenDurationMs: duration });
    }
  };

  document.addEventListener('visibilitychange', onVisibility);

  window.addEventListener('pageshow', (e: PageTransitionEvent) => {
    if (e.persisted) {
      wasHiddenOrBackground = false;
      hiddenAt = null;
      notify({ reason: 'pageshow', hiddenDurationMs: null });
    }
  });
}

export function subscribePortalResume(handler: (detail: PortalResumeDetail) => void): () => void {
  ensureListeners();
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}
